import { useGetAllCriticality } from "@features/ax/assetRegistry/systems/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { HelpCircle } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
interface CriticalityItem {
  name: string;
  code: string;
  description: string;
  weightage: number;
  isActive: boolean;
  score?: number;
}

interface CriticalityAssessmentData {
  criticalityScores: Record<string, number>;
  criticalityAssessment: Array<{
    name: string;
    score: number;
    code: string;
    weight: number;
  }>;
  finalScore: number;
  level: string;
}

interface CriticalityAssessmentModalProps {
  open: boolean;
  onClose: () => void;
  existingScores?: Record<string, number>;
  existingFinalScore?: number;
  isUpdating: boolean;
  onSave: (data: CriticalityAssessmentData) => void;
  title?: string;
  entityName?: string;
}

const CriticalityAssessmentModal: React.FC<CriticalityAssessmentModalProps> = ({
  open,
  onClose,
  existingScores,
  existingFinalScore,
  isUpdating,
  onSave,
  title = "Criticality Assessment",
  entityName,
}) => {
  const dataLoadedRef = useRef(false);

  // Fetch criticality data from API
  const { data: criticality } = useGetAllCriticality();

  // Initialize dynamic criticality fields with default value of 3
  const initializeDynamicFields = useMemo(() => {
    if (!criticality || !Array.isArray(criticality)) return {};

    const dynamicFields: Record<string, number> = {};
    criticality.forEach((item) => {
      const fieldKey = item.code.split("#")[0];
      dynamicFields[fieldKey] = 3; // Default to 3
    });

    return dynamicFields;
  }, [criticality]);

  // Local state for form data
  const [localFormData, setLocalFormData] = useState<Record<string, number>>(
    initializeDynamicFields
  );

  // Generate criticality slider fields dynamically from API data
  const generateCriticalityFields = useMemo((): FormField[] => {
    if (!criticality || !Array.isArray(criticality)) return [];

    return criticality
      .filter((item) => item.isActive === true)
      .map((item) => {
        const fieldKey = item.code.split("#")[0];

        return {
          name: fieldKey,
          type: "slider" as const,
          label: `${item.name} (${item.weightage}%)`,
          min: 1,
          max: 5,
          step: 1,
          defaultValue: 3,
          showHelperTooltip: true,
          tooltipText: item.description || `Assessment for ${item.name}`,
          className: "col-span-2",
        } as FormField;
      });
  }, [criticality]);

  // Create form instance
  const form = FormService.createForm({
    fields: generateCriticalityFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: localFormData,
  });

  // Initialize form data when modal opens
  useEffect(() => {
    if (open && !dataLoadedRef.current) {
      if (existingScores && Object.keys(existingScores).length > 0) {
        // Use existing scores if provided (edit mode)
        setLocalFormData(existingScores);

        // Update form values with existing scores
        Object.keys(existingScores).forEach((key) => {
          try {
            form.setValue(key, existingScores[key]);
          } catch (error) {
            console.error(`Failed to set form value for ${key}:`, error);
          }
        });
      } else {
        // Use default values
        const defaultData = initializeDynamicFields;
        setLocalFormData(defaultData);

        // Update form values with defaults
        Object.keys(defaultData).forEach((key) => {
          try {
            form.setValue(key, defaultData[key]);
          } catch (error) {
            console.error(`Failed to set form value for ${key}:`, error);
          }
        });
      }
      dataLoadedRef.current = true;
    }
  }, [open, existingScores, initializeDynamicFields, form]);

  // Reset data loaded flag when modal closes
  useEffect(() => {
    if (!open) {
      dataLoadedRef.current = false;
    }
  }, [open]);

  // Get criticality field keys
  const criticalityKeys = useMemo(
    () =>
      (criticality ?? [])
        .filter((i) => i.isActive)
        .map((i) => i.code.split("#")[0]),
    [criticality]
  );

  // Watch slider fields so component re-renders when they change
  const watchedValues = form.watch(criticalityKeys);

  // Calculate criticality score
  const { score: criticalityScore, level: criticalityLevel } = useMemo(() => {
    try {
      if (!criticality) return { score: 0, level: "Low" };

      const activeItems = criticality.filter((item) => item.isActive === true);
      if (activeItems.length === 0) return { score: 0, level: "Low" };

      let totalWeightage = 0;
      let weightedSum = 0;

      activeItems.forEach((item) => {
        const fieldKey = item.code.split("#")[0];
        const scoreValue = form.getValues(fieldKey);

        if (
          scoreValue !== undefined &&
          scoreValue !== null &&
          !isNaN(Number(scoreValue))
        ) {
          const weight = item.weightage / 100;
          weightedSum += Number(scoreValue) * weight;
          totalWeightage += weight;
        }
      });

      const criticalityScore =
        totalWeightage > 0 ? weightedSum / totalWeightage : 0;

      let criticalityLevel: string;
      if (criticalityScore >= 4) criticalityLevel = "High";
      else if (criticalityScore >= 3) criticalityLevel = "Medium";
      else if (criticalityScore >= 2) criticalityLevel = "Medium";
      else if (criticalityScore >= 1) criticalityLevel = "Low";
      else criticalityLevel = "Low";

      return { score: criticalityScore, level: criticalityLevel };
    } catch (error) {
      return { score: 0, level: "Low" };
    }
  }, [criticality, watchedValues, form]);

  // Get criticality badge variant
  const getCriticality = (score: number) => {
    if (score >= 0 && score <= 25) {
      return { level: "Low", variant: "success" as const };
    } else if (score <= 50) {
      return { level: "Medium", variant: "warning" as const };
    } else if (score <= 75) {
      return { level: "High", variant: "destructive" as const };
    } else {
      return { level: "Critical", variant: "secondary" as const };
    }
  };

  // Handle field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    const updatedData = {
      ...localFormData,
      [fieldName]: value,
    };

    setLocalFormData(updatedData);
  };

  // Handle Save
  const handleSave = () => {
    const formData = form.getValues();

    // Build criticality assessment array
    const criticalityAssessment =
      criticality
        ?.filter((item) => item.isActive === true)
        .map((item) => {
          const fieldKey = item.code.split("#")[0];
          return {
            name: item.name,
            score: formData[fieldKey] || 3,
            code: item.code,
            weight: item.weightage,
          };
        }) || [];

    const savedData: CriticalityAssessmentData = {
      criticalityScores: formData,
      criticalityAssessment,
      finalScore: Math.round(criticalityScore * 20),
      level: criticalityLevel,
    };

    onSave(savedData);
  };

  // Handle Cancel
  const handleCancel = () => {
    onClose();
  };

  const { level, variant } = getCriticality(criticalityScore * 20);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {title}
            {entityName && (
              <span className="text-gray-600 font-normal ml-2">
                - {entityName}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Criticality Assessment Form */}
          <div>
            <h3 className="text-lg font-medium mb-4">
              Criticality Assessment Parameters
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Adjust the sliders below to assess each criticality parameter.
              Each parameter is weighted and contributes to the overall
              criticality score.
            </p>

            {generateCriticalityFields.length > 0 ? (
              <DynamicForm
                fields={generateCriticalityFields}
                form={form}
                onFieldChange={handleFieldChange}
                gridColumns={2}
                responsiveColumns={{
                  sm: 1,
                  md: 2,
                }}
                config={{
                  layout: "grid",
                  columns: 2,
                  showResetButton: false,
                  showSubmitButton: false,
                  spacing: "medium",
                  classes: {
                    form: "space-y-6 p-0",
                    slider: "w-full",
                    label: "block text-sm font-medium text-gray-700",
                    container: "space-y-1",
                    helperText: "text-sm text-gray-500 mt-1",
                  },
                }}
              />
            ) : (
              <div className="text-center py-8 text-gray-500">
                No criticality parameters available.
              </div>
            )}
          </div>

          {/* Final Criticality Score Display */}
          <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-sm font-medium text-gray-700">
                    Final Criticality Score
                  </p>
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-muted-foreground cursor-pointer" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          This score is automatically calculated based on the
                          weighted average of all assessed parameters, providing
                          an overall measure of criticality. The score helps
                          prioritize maintenance activities, emergency response
                          planning, and resource allocation decisions.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {Math.round(criticalityScore * 20)}
                  <span className="text-lg text-gray-500 ml-1">/ 100</span>
                </p>
              </div>
              <Badge variant={variant} className="text-lg px-4 py-2">
                {level}
              </Badge>
            </div>
          </div>

          {/* Assessment Guidelines */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">
              Assessment Guidelines
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>
                • <strong>Score 1:</strong> Minimal impact on operations
              </li>
              <li>
                • <strong>Score 2:</strong> Low impact, can tolerate downtime
              </li>
              <li>
                • <strong>Score 3:</strong> Moderate impact, requires planning
              </li>
              <li>
                • <strong>Score 4:</strong> High impact, critical for operations
              </li>
              <li>
                • <strong>Score 5:</strong> Mission critical, immediate action
                required
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button isLoading={isUpdating} onClick={handleSave}>
              Save Assessment
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CriticalityAssessmentModal;
